import type {
    FilmoscopeDrmConfig,
    AudioTrack,
    TextTrack,
    VideoTrack,
    FilmoscopePreferredTracks,
    FilmoscopeStream,
    FilmoscopeSource,
} from '@kinopoisk-int/filmoscope';

import { DeepReadonly } from '@/scenarios/legacy-player/lib/deepReadonly';

import { MAX_VOLUME } from '../../constants/volume';
import { VideoTrack as LocalVideoTrack } from '../../types/instance';
import { DRM_SERVER_NAME } from '../../constants/drm';
import { AutoplayPolicy, DrmConfig, PreferredTracks, Source, Stream } from '../../types/config';

export function transformPlayerVolumeToLocal(volumeLevel: number): number {
    return Math.trunc(volumeLevel * MAX_VOLUME);
}

export function transformLocalVolumeToPlayer(volumeLevel: number): number | undefined {
    return volumeLevel / MAX_VOLUME;
}

export function transformLocalSourceToFilmoscope(
    source: Source,
    autoplayPolicy?: AutoplayPolicy
): FilmoscopeSource {
    const {
        streams,
        additionalParams,
        userPreferences: { preferredTracks },
        id,
        ...rest
    } = source;

    return {
        ...rest,
        id,
        streams: streams.map((stream) => transformStream(stream)),
        preferredTracks: preferredTracks && transformPreferredTracks(preferredTracks),
        videoContentId: id,
        mediaFormat: 'video',
        allowedMuted: autoplayPolicy === 'Allowed',
        from: additionalParams.from,
        fromBlock: additionalParams.from_block,
        streamBlock: additionalParams.stream_block,
        puid: additionalParams.puid,
    };
}

function transformPreferredTracks({
    textTrackLang: textTrack,
    audioTrackLang: audioTrack,
    videoTrackLabel: videoTrack,
}: PreferredTracks): FilmoscopePreferredTracks {
    return {
        preferredTextTrack: textTrack,
        preferredAudioTrack: audioTrack,
        preferredVideoTrack: videoTrack,
    };
}

export function transformStreamToLocal(
    stream: FilmoscopeStream
): DeepReadonly<Omit<Stream, 'drmConfig'>> {
    const { drmConfig, url, ...rest } = stream;

    return { url, ...rest };
}

function transformStream({ url, drmConfig, audio, subtitles }: Stream): FilmoscopeStream {
    const stream: FilmoscopeStream = {
        url,
        audio,
        subtitles,
    };

    if (drmConfig) {
        stream.drmConfig = transformDrmConfig(drmConfig);
    }

    return stream;
}

function transformDrmConfig(drmConfig: DrmConfig): FilmoscopeDrmConfig | undefined {
    const drmServers = drmConfig.servers;

    if (!drmServers) {
        return undefined;
    }

    const filmoscopeDrmConfig: FilmoscopeDrmConfig = {
        servers: Object.values(DRM_SERVER_NAME).reduce<FilmoscopeDrmConfig['servers']>(
            (acc, drmServerName) => {
                let url;

                if (drmServerName === DRM_SERVER_NAME.fairPlay) {
                    url = drmConfig.advanced?.[DRM_SERVER_NAME.fairPlay]?.processSPCPath;
                } else {
                    url = drmServers[drmServerName];
                }

                if (url) {
                    acc[drmServerName] = url;
                }

                return acc;
            },
            {}
        ),
        requestParams: drmConfig?.requestParams,
    };

    const fairPlayServerCertificateUrl = drmServers[DRM_SERVER_NAME.fairPlay];

    if (fairPlayServerCertificateUrl) {
        filmoscopeDrmConfig.advanced = {
            [DRM_SERVER_NAME.fairPlay]: {
                serverCertificateUri: fairPlayServerCertificateUrl,
            },
        };
    }

    return filmoscopeDrmConfig;
}

export function transformFilmoscopeSourceToLocal(
    filmoscopeSource: FilmoscopeSource,
    vsid: string
): Source {
    const { streams, from, fromBlock, streamBlock, puid, preferredTracks, subtitles, id } =
        filmoscopeSource;

    const { preferredAudioTrack, preferredTextTrack, preferredVideoTrack } = preferredTracks ?? {};

    return {
        ...filmoscopeSource,
        id: id ?? 'Unknown', // never gonna happen
        additionalParams: {
            from: from || '',
            from_block: fromBlock,
            stream_block: streamBlock,
            vsid,
            puid,
        },
        userPreferences: {
            preferredTracks: {
                textTrackLang: preferredTextTrack,
                audioTrackLang: preferredAudioTrack,
                videoTrackLabel: preferredVideoTrack,
            },
        },
        streams: streams.map((stream) => ({
            ...stream,
            drmConfig:
                stream.drmConfig === undefined ? undefined : { servers: stream.drmConfig.servers },
            subtitles,
        })),
    };
}

export function transformAudioTrackToLocal(filmoscopeAudioTracks: AudioTrack) {
    const { label, value, language, bitrate } = filmoscopeAudioTracks;
    return {
        name: label || '',
        value,
        lang: language,
        bitrate,
    };
}

export function transformTextTrackToLocal(filmoscopeTextTracks: TextTrack) {
    const { label, value, language } = filmoscopeTextTracks;
    return {
        name: label || '',
        value,
        lang: language,
    };
}

export function transformVideoTrackToLocal(
    filmoscopeVideoTracks: VideoTrack,
    isAuto: boolean = false
): LocalVideoTrack {
    const { label, value, bitrate, height, width } = filmoscopeVideoTracks;
    return {
        name: label || '',
        value,
        bitrate: bitrate || 0,
        height,
        width,
        auto: isAuto,
    };
}
